import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { RotateCcw } from 'lucide-react';
import { fetchBranches, fetchInvoiceItems, createSupplierReturn } from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import type { SupplierInvoice } from '@/types/supplier';

interface Branch {
  id: string;
  name: string;
  isMain: boolean;
}

interface ReturnLine {
  item_id: string;
  item_name: string;
  item_sku?: string;
  invoiceQuantity: number;
  returnQuantity: number;
  unit_price: number;
}

interface ReturnItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: SupplierInvoice | null;
  onCompleted?: () => void;
}

export function ReturnItemDialog({ open, onOpenChange, invoice, onCompleted }: ReturnItemDialogProps) {
  const { user } = useAuth();
  const currencySymbol = useCurrencySymbol();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<ReturnLine[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchBranches().then(res => {
      const list = res.data || [];
      setBranches(list);
      const main = list.find((b: Branch) => b.isMain);
      setSelectedBranch(main?.id || list[0]?.id || '');
    }).catch(() => setBranches([]));
  }, [open]);

  useEffect(() => {
    if (!open || !invoice) return;
    setDate(format(new Date(), 'yyyy-MM-dd'));
    setReason('');
    setLoadingItems(true);
    fetchInvoiceItems(invoice.id)
      .then(res => {
        const raw = res.data?.data || res.data || [];
        setLines(raw.map((item: any) => ({
          item_id: item.item_id,
          item_name: item.item_name,
          item_sku: item.item_sku,
          invoiceQuantity: Number(item.quantity) || 0,
          returnQuantity: 0,
          unit_price: Number(item.unit_price) || 0,
        })));
      })
      .catch(() => {
        toast.error('Failed to load this invoice\'s items');
        setLines([]);
      })
      .finally(() => setLoadingItems(false));
  }, [open, invoice]);

  const updateLine = (itemId: string, field: 'returnQuantity' | 'unit_price', value: number) => {
    setLines(prev => prev.map(l => l.item_id === itemId ? { ...l, [field]: value } : l));
  };

  const totalReturn = lines.reduce((sum, l) => sum + l.returnQuantity * l.unit_price, 0);
  const selectedLines = lines.filter(l => l.returnQuantity > 0);

  const handleSubmit = async () => {
    if (!invoice) return;
    if (!selectedBranch) {
      toast.error('Please select a branch');
      return;
    }
    if (selectedLines.length === 0) {
      toast.error('Enter a quantity for at least one item to return');
      return;
    }
    for (const line of selectedLines) {
      if (line.returnQuantity > line.invoiceQuantity) {
        toast.error(`Can't return more of "${line.item_name}" than was on this invoice (${line.invoiceQuantity})`);
        return;
      }
      if (line.unit_price < 0) {
        toast.error(`Price for "${line.item_name}" can't be negative`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await createSupplierReturn({
        supplier_id: invoice.supplierId,
        invoice_id: invoice.id,
        branch_id: selectedBranch,
        date,
        reason: reason || undefined,
        user_id: user?.id,
        items: selectedLines.map(l => ({
          item_id: l.item_id,
          quantity: l.returnQuantity,
          unit_price: l.unit_price,
        })),
      });
      toast.success('Return recorded and supplier credit updated');
      onOpenChange(false);
      onCompleted && onCompleted();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to create return');
    } finally {
      setSubmitting(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Return Item — Invoice {invoice.invoiceNumber}
          </DialogTitle>
          <DialogDescription>
            Choose the quantity of each item being returned. Prices default to what was
            invoiced but stay editable. Returned stock is deducted from the branch you
            select, and the total is credited toward this supplier's next invoice.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Branch (stock deducted from here) *</Label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger>
                  <SelectValue placeholder="Select branch..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Return Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Defective" />
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Item</TableHead>
                  <TableHead className="text-center w-28">Invoiced Qty</TableHead>
                  <TableHead className="text-center w-28">Return Qty</TableHead>
                  <TableHead className="text-right w-32">Unit Price</TableHead>
                  <TableHead className="text-right w-28">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingItems ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Loading items...
                    </TableCell>
                  </TableRow>
                ) : lines.map(line => (
                  <TableRow key={line.item_id}>
                    <TableCell>
                      <div className="font-medium">{line.item_name}</div>
                      {line.item_sku && <div className="text-xs text-muted-foreground">SKU: {line.item_sku}</div>}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">{line.invoiceQuantity}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max={line.invoiceQuantity}
                        value={line.returnQuantity === 0 ? '' : line.returnQuantity}
                        onChange={(e) => updateLine(line.item_id, 'returnQuantity', e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0))}
                        className="h-8 w-20 text-center mx-auto"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={line.unit_price}
                        onChange={(e) => updateLine(line.item_id, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="h-8 w-28 text-right ml-auto"
                      />
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {currencySymbol}{(line.returnQuantity * line.unit_price).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end text-lg font-semibold">
            Return Total: {currencySymbol}{totalReturn.toFixed(2)}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Processing...' : 'Create Return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
